import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_DARK_URL } from "@/lib/landingData";
import { LayoutDashboard, Target, MessageSquare, GraduationCap,
  Settings, LogOut, Shield, CalendarCheck, Calendar, Users, Sparkles, ImageIcon, Trophy, Activity, Mic, BookOpen, Video, MoreHorizontal, X } from "lucide-react";
import NotificationsBell from "@/components/app/NotificationsBell";

const nav = [
  { to: "/app", label: "Today", icon: LayoutDashboard, end: true },
  { to: "/app/goals", label: "Goals", icon: Target },
  { to: "/app/coach", label: "Coach", icon: MessageSquare },
  { to: "/app/journal", label: "Journal", icon: BookOpen },
  { to: "/app/voice", label: "Voice", icon: Mic },
  { to: "/app/lifescore", label: "Life Score", icon: Activity },
  { to: "/app/learn", label: "Learn", icon: GraduationCap },
  { to: "/app/sports", label: "Sports", icon: Video },
  { to: "/app/calendar", label: "Calendar", icon: Calendar },
  { to: "/app/vision", label: "Vision", icon: ImageIcon },
  { to: "/app/student", label: "Student", icon: Sparkles },
  { to: "/app/family", label: "Family", icon: Users },
  { to: "/app/community", label: "Community", icon: Trophy },
  { to: "/app/reviews", label: "Reviews", icon: CalendarCheck },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-2xl px-4 py-3 font-nunito text-sm font-bold transition-colors duration-200 ${
    isActive ? "bg-[#FFE8E3] text-[#C2410C]" : "text-[#718096] hover:bg-[#FFF9F5] hover:text-[#FF7E67]"
  }`;

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Mobile bottom nav: 4 primary tabs + "More" button that opens the full menu.
  const primaryMobile = nav.slice(0, 4);
  const moreItems = nav.slice(4);

  return (
    <div className="relative z-10 flex min-h-screen bg-[#FFF9F5]">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-72 flex-col border-r border-[#FFE8E3] bg-white p-5 lg:flex">
        <button onClick={() => navigate("/app")} className="mb-8 flex items-center px-2" data-testid="app-logo">
          <img src={LOGO_DARK_URL} alt="b→come" className="h-10 w-auto" />
        </button>
        <nav className="flex-1 space-y-1.5">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase()}`} className={linkCls}>
                <Icon className="h-5 w-5" strokeWidth={2.5} /> {n.label}
              </NavLink>
            );
          })}
          {user?.role === "admin" && (
            <NavLink to="/app/admin" data-testid="nav-admin" className={linkCls}>
              <Shield className="h-5 w-5" strokeWidth={2.5} /> Admin
            </NavLink>
          )}
        </nav>
        <div className="mt-4 border-t border-[#FFE8E3] pt-4">
          {user?.plan === "free" && (
            <button onClick={() => navigate("/pricing")} data-testid="sidebar-upgrade-btn"
              className="mb-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-sunrise px-4 py-2.5 font-nunito text-sm font-bold text-white shadow-glow-coral transition-transform duration-200 hover:scale-[1.02]">
              <Sparkles className="h-4 w-4" strokeWidth={2.5} /> Upgrade — 7-day free trial
            </button>
          )}
          <div className="mb-3 flex items-center gap-3 px-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sunrise font-nunito text-base font-extrabold text-white">
              {(user?.name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate font-nunito text-sm font-bold text-[#2D3748]">{user?.name}</p>
              <p className="truncate font-quicksand text-xs font-semibold capitalize text-[#718096]">{user?.plan} plan</p>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 font-quicksand text-sm font-semibold text-[#718096] transition-colors duration-200 hover:bg-[#FFF9F5] hover:text-[#FF7E67]">
            <LogOut className="h-4 w-4" strokeWidth={2.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-[#FFE8E3] bg-white px-4 py-3 lg:hidden">
        <img src={LOGO_DARK_URL} alt="b→come" className="h-8 w-auto" />
        <div className="flex items-center gap-3">
          <NotificationsBell testid="notifications-bell-mobile" />
          <button onClick={handleLogout} className="text-[#718096]" data-testid="logout-btn-mobile">
            <LogOut className="h-5 w-5" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Desktop notifications bell */}
      <div className="fixed right-8 top-6 z-40 hidden lg:block">
        <NotificationsBell testid="notifications-bell-desktop" />
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-[#FFE8E3] bg-white py-2 lg:hidden">
        {primaryMobile.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex flex-col items-center gap-1 px-2 py-1 font-nunito text-[10px] font-bold ${isActive ? "text-[#C2410C]" : "text-[#718096]"}`}>
              <Icon className="h-5 w-5" strokeWidth={2.5} /> {n.label}
            </NavLink>
          );
        })}
        <button onClick={() => setMoreOpen(true)}
          data-testid="mobile-more-btn"
          className="flex flex-col items-center gap-1 px-2 py-1 font-nunito text-[10px] font-bold text-[#718096]">
          <MoreHorizontal className="h-5 w-5" strokeWidth={2.5} /> More
        </button>
      </nav>

      {/* Mobile "More" sheet — every feature reachable */}
      {moreOpen && (
        <div className="fixed inset-0 z-[70] flex items-end bg-[#2D3748]/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMoreOpen(false)}
          data-testid="mobile-more-sheet">
          <div className="w-full rounded-t-3xl bg-white p-5 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <p className="font-nunito text-lg font-extrabold text-[#2D3748]">All features</p>
              <button onClick={() => setMoreOpen(false)}
                className="rounded-full p-2 text-[#718096] hover:bg-[#FFF9F5]"
                data-testid="mobile-more-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {moreItems.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink key={n.to} to={n.to} end={n.end}
                    onClick={() => setMoreOpen(false)}
                    data-testid={`mobile-more-${n.label.toLowerCase()}`}
                    className={({ isActive }) => `flex flex-col items-center justify-center gap-1.5 rounded-2xl border p-3 text-center transition-all ${
                      isActive ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C]"
                               : "border-[#FFE8E3] bg-[#FFF9F5] text-[#4A5568] hover:border-[#FF7E67]"
                    }`}>
                    <Icon className="h-5 w-5" strokeWidth={2.5} />
                    <span className="font-nunito text-xs font-bold">{n.label}</span>
                  </NavLink>
                );
              })}
              {user?.role === "admin" && (
                <NavLink to="/app/admin"
                  onClick={() => setMoreOpen(false)}
                  data-testid="mobile-more-admin"
                  className={({ isActive }) => `flex flex-col items-center justify-center gap-1.5 rounded-2xl border p-3 text-center transition-all ${
                    isActive ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C]"
                             : "border-[#FFE8E3] bg-[#FFF9F5] text-[#4A5568] hover:border-[#FF7E67]"
                  }`}>
                  <Shield className="h-5 w-5" strokeWidth={2.5} />
                  <span className="font-nunito text-xs font-bold">Admin</span>
                </NavLink>
              )}
            </div>
            {user?.plan === "free" && (
              <button onClick={() => { setMoreOpen(false); navigate("/pricing"); }}
                data-testid="mobile-more-upgrade"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-sunrise px-4 py-3 font-nunito text-sm font-bold text-white shadow-glow-coral">
                <Sparkles className="h-4 w-4" strokeWidth={2.5} /> Upgrade — 7-day free trial
              </button>
            )}
          </div>
        </div>
      )}

      <main className="flex-1 pb-24 pt-16 lg:ml-72 lg:pb-0 lg:pt-0">
        <div className="mx-auto max-w-6xl px-5 py-8 font-quicksand lg:px-12 lg:py-12">{children}</div>
      </main>
    </div>
  );
}
